import { sendEvent } from "@/lib/utils/send-gtm";
import type {
  TravelInformationEvent,
  PurchaseStartEvent,
  TravelersInformationEvent,
  AddTravelerEvent,
  DeleteTravelerEvent,
  TravelQuotesReceivedEvent,
  ViewItemListEvent,
  TravelQuoteViewedEvent,
  ViewQuoteFeaturesEvent,
  TravelQuoteSelectedEvent,
  ViewTravelInfoEvent,
  ShareQuoteListEvent,
  TravelQuoteAddonSelectedEvent,
  AddAdditionalQuoteFeaturesEvent,
  SelectItemEvent,
  TravelPayClickedEvent,
  TravelPolicyPaymentStartedEvent,
  BeginCheckoutEvent,
  TravelPolicyPaymentSuccessEvent,
  PurchaseEvent,
  TravelPolicyPaymentFailedEvent,
  NavigationMenuEvent,
  PurchasedPolicyOptionEvent,
  TravelPolicyActionEvent,
  LoginEvent,
} from "@/lib/analytics/types";

// ---------------------------------------------------------------------------
// Travel Journey Events
// ---------------------------------------------------------------------------

/** Step 1 — user submits travel info (trip type, purpose, destination) */
export const trackTravelInformation = (data: TravelInformationEvent) =>
  sendEvent({ event: "Travel Information", ...data });

/** Step 1 — GA4 ecommerce: journey started */
export const trackPurchaseStart = (data: PurchaseStartEvent) =>
  sendEvent({ event: "purchase_start", ...data });

// ---------------------------------------------------------------------------
// Travelers Info Events
// ---------------------------------------------------------------------------

/** Step 2 — user continues past traveler info form */
export const trackTravelersInformation = (data: TravelersInformationEvent) =>
  sendEvent({ event: "Travelers Information", ...data });

/** Step 2 — user adds a single traveler */
export const trackAddTraveler = (data: AddTravelerEvent) =>
  sendEvent({ event: "add_traveler", ...data });

/** Step 2 — user deletes a traveler */
export const trackDeleteTraveler = (data: DeleteTravelerEvent) =>
  sendEvent({ event: "delete_traveler", ...data });

// ---------------------------------------------------------------------------
// Quote List Events
// ---------------------------------------------------------------------------

/** Step 3 — fires once when quote polling settles (all quotes received) */
export const trackTravelQuotesReceived = (data: TravelQuotesReceivedEvent) =>
  sendEvent({ event: "Travel Quotes Received", ...data });

/** Step 3 — GA4 ecommerce: quote list rendered */
export const trackViewItemList = (data: ViewItemListEvent) =>
  sendEvent({ event: "view_item_list", ...data });

/** Step 3 — user clicks "View All Features" arrow on a quote card */
export const trackTravelQuoteViewed = (data: TravelQuoteViewedEvent) =>
  sendEvent({ event: "Travel Quote Viewed", ...data });

/** Step 3 — GA4: user opens view-features sheet */
export const trackViewQuoteFeatures = (data: ViewQuoteFeaturesEvent) =>
  sendEvent({ event: "view_quote_features", ...data });

/** Step 3 — user clicks "Select" on a quote card */
export const trackTravelQuoteSelected = (data: TravelQuoteSelectedEvent) =>
  sendEvent({ event: "Travel Quote Selected", ...data });

/** Step 3 — user views trip details sheet */
export const trackViewTravelInfo = (data: ViewTravelInfoEvent) =>
  sendEvent({ event: "view_travel_info", ...data });

/** Step 3 — user shares quote list */
export const trackShareQuoteList = (data: ShareQuoteListEvent) =>
  sendEvent({ event: "share_quote_list", ...data });

// ---------------------------------------------------------------------------
// Add-on Events
// ---------------------------------------------------------------------------

/** Step 3 — user checks an add-on feature on a quote */
export const trackTravelQuoteAddonSelected = (
  data: TravelQuoteAddonSelectedEvent,
) => sendEvent({ event: "Travel Quote Add-on Selected", ...data });

/** Step 3 — GA4: user confirms selected add-ons and proceeds */
export const trackAddAdditionalQuoteFeatures = (
  data: AddAdditionalQuoteFeaturesEvent,
) => sendEvent({ event: "add_additional_quote_features", ...data });

/** Step 3 — GA4 ecommerce: user selects a quote item */
export const trackSelectItem = (data: SelectItemEvent) =>
  sendEvent({ event: "select_item", ...data });

// ---------------------------------------------------------------------------
// Payment Events
// ---------------------------------------------------------------------------

/** Step 5 — user clicks Pay Now on summary page */
export const trackTravelPayClicked = (data: TravelPayClickedEvent) =>
  sendEvent({ event: "Travel Pay Clicked", ...data });

/** Step 5 — GA4 ecommerce: checkout started */
export const trackBeginCheckout = (data: BeginCheckoutEvent) =>
  sendEvent({ event: "begin_checkout", ...data });

/** Step 5 — user initiates payment (after Pay Clicked, before success/failure) */
export const trackTravelPolicyPaymentStarted = (
  data: TravelPolicyPaymentStartedEvent,
) => sendEvent({ event: "Travel Policy Payment Started", ...data });

// ---------------------------------------------------------------------------
// Post-Payment Events
// ---------------------------------------------------------------------------

/** Success page — policy issued successfully */
export const trackTravelPolicyPaymentSuccess = (
  data: TravelPolicyPaymentSuccessEvent,
) => sendEvent({ event: "Travel Policy Payment Success", ...data });

/** Success page — GA4 ecommerce: purchase completed */
export const trackPurchase = (data: PurchaseEvent) =>
  sendEvent({ event: "purchase", ...data });

/** Failure page — payment failed */
export const trackTravelPolicyPaymentFailed = (
  data: TravelPolicyPaymentFailedEvent,
) => sendEvent({ event: "Travel Policy Payment Failed", ...data });

// ---------------------------------------------------------------------------
// Dashboard Events
// ---------------------------------------------------------------------------

/** Dashboard — user clicks a navigation menu tab */
export const trackNavigationMenu = (data: NavigationMenuEvent) =>
  sendEvent({ event: "Navigation Menu", ...data });

/** Dashboard — user switches the purchased policy status tab */
export const trackPurchasedPolicyOption = (data: PurchasedPolicyOptionEvent) =>
  sendEvent({ event: "Purchased Policy Option", ...data });

/** Dashboard — user clicks an action on a purchased policy card */
export const trackTravelPolicyAction = (data: TravelPolicyActionEvent) =>
  sendEvent({ event: "Travel Policy Action", ...data });

// ---------------------------------------------------------------------------
// Auth Events
// ---------------------------------------------------------------------------

/** User logs in successfully. Fired once per sign-in by PostLoginTracker. */
export const trackLogin = (data: LoginEvent) => {
  sendEvent({ event: "login", flow_name: "travel", ...data });

  if (typeof window !== "undefined") {
    const w = window as unknown as {
      dataLayer?: unknown[];
      google_tag_manager?: Record<string, unknown>;
    };
    // One-shot diagnostic to confirm the event reached the dataLayer / GTM.
    console.info("[GTM] login event dispatched", {
      user_id: data.user_id,
      dataLayerEntries: Array.isArray(w.dataLayer) ? w.dataLayer.length : 0,
      gtmContainerLoaded: !!w.google_tag_manager,
    });
  }
};

/** User logs out */
export const trackLogout = () =>
  sendEvent({ event: "logout", flow_name: "travel" });
